import { createSlice } from '@reduxjs/toolkit';
import * as robotCleaningControlActionTypes from './robotCleaningControlActions.types';
import type { RobotCleaningControlState } from './types/RobotCleaningControl.types';

export const initialState: RobotCleaningControlState = {
  isLoading: false,
  pendingCommandType: null,
  error: null,
  errorCommandType: null,
  isPaused: null,
  pendingLocateCommand: null,
  isBeepOn: null,
  isBlinkOn: null,
  lastRouteSkippedTaskId: null,
  praDeviceId: null,
};

const robotCleaningControlSlice = createSlice({
  name: 'robot-cleaning-control',
  initialState,
  reducers: {
    sendCleaningCommandRequest: (state, action: robotCleaningControlActionTypes.SendCleaningCommandRequestAction) => {
      // The saga uses takeLeading and ignores a request while one is already in flight — guard the
      // reducer the same way, otherwise this would overwrite pendingCommandType with a command that
      // never actually gets sent, showing the wrong action as pending.
      if (state.isLoading) {
        return state;
      }

      state.isLoading = true;
      state.pendingCommandType = action.payload.commandType;
      state.error = null;
      state.errorCommandType = null;
      return state;
    },
    sendCleaningCommandSuccess: (state, _action: robotCleaningControlActionTypes.SendCleaningCommandSuccessAction) => {
      // Paused/resumed comes from the API (paused-state query + subscription), not from the command result.
      state.isLoading = false;
      state.pendingCommandType = null;
      return state;
    },
    sendCleaningCommandError: (state, action: robotCleaningControlActionTypes.SendCleaningCommandErrorAction) => {
      state.errorCommandType = state.pendingCommandType;
      state.isLoading = false;
      state.pendingCommandType = null;
      state.error = action.payload.error;
      return state;
    },
    dismissCleaningCommandError: state => {
      state.error = initialState.error;
      state.errorCommandType = initialState.errorCommandType;
      return state;
    },
    lastRouteSkipped: (state, action: robotCleaningControlActionTypes.LastRouteSkippedAction) => {
      state.lastRouteSkippedTaskId = action.payload;
      return state;
    },
    sendLocateCommandRequest: (state, action: robotCleaningControlActionTypes.SendLocateCommandRequestAction) => {
      // Same guard as the cleaning command: the saga ignores a request while one is in flight.
      if (state.pendingLocateCommand) {
        return state;
      }

      state.pendingLocateCommand = action.payload.commandType;
      return state;
    },
    sendLocateCommandSuccess: (state, _action: robotCleaningControlActionTypes.SendLocateCommandSuccessAction) => {
      state.pendingLocateCommand = null;
      return state;
    },
    sendLocateCommandError: (state, _action: robotCleaningControlActionTypes.SendLocateCommandErrorAction) => {
      state.pendingLocateCommand = null;
      return state;
    },
    sendCleaningCommandReset: state => {
      state.isLoading = initialState.isLoading;
      state.pendingCommandType = initialState.pendingCommandType;
      state.error = initialState.error;
      state.errorCommandType = initialState.errorCommandType;
      state.pendingLocateCommand = initialState.pendingLocateCommand;
      state.lastRouteSkippedTaskId = initialState.lastRouteSkippedTaskId;
      return state;
    },
    subscribeToPausedState: (_state, _action: robotCleaningControlActionTypes.SubscribeToPausedStateAction) =>
      undefined,
    // The device's state changed (cleaning, docking, idle...); the machine details refetch the robot status from it.
    deviceStateReported: (_state, _action: robotCleaningControlActionTypes.DeviceStateReportedAction) => undefined,
    // What the device reported comes with the subscription, so it is dropped when the subscription stops (the robot
    // changes or the controls go away), not by a command reset that can run while the subscription stays open.
    unsubscribeFromPausedState: state => {
      state.isPaused = initialState.isPaused;
      state.isBeepOn = initialState.isBeepOn;
      state.isBlinkOn = initialState.isBlinkOn;
      state.praDeviceId = initialState.praDeviceId;
      return state;
    },
    locateSettingsReceived: (state, action: robotCleaningControlActionTypes.LocateSettingsReceivedAction) => {
      const { beep, blink } = action.payload;

      state.isBeepOn = beep ?? state.isBeepOn;
      state.isBlinkOn = blink ?? state.isBlinkOn;
      return state;
    },
    praDeviceIdReceived: (state, action: robotCleaningControlActionTypes.PraDeviceIdReceivedAction) => {
      state.praDeviceId = action.payload;
      return state;
    },
    pausedStateReceived: (state, action: robotCleaningControlActionTypes.PausedStateReceivedAction) => {
      state.isPaused = action.payload;
      return state;
    },
  },
});

export const RobotCleaningControlActions = {
  ...robotCleaningControlSlice.actions,
};
export const robotCleaningControlReducer = robotCleaningControlSlice.reducer;
